import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PlusIcon } from '../../assets/icons/icons';
import ActionButton from '../../components/shared/ActionButton';
import IconButton from '../../components/shared/IconButton';
import { apiConnecter } from '../../services/apiConnector';
import { useSelector } from 'react-redux';
import CreateOrganizerForm from '../../components/forms/CreateOrganizerForm';
import Modal from '../../components/shared/ErrorModal';
import { toast } from "sonner";
import SearchBox from '../../components/shared/SearchBox';
import InfiniteScroll from 'react-infinite-scroll-component';
import Badge from '../../components/shared/Badge';
import API_ENDPOINTS from '../../constant/EndPoints';
const MAX_HEIGHT_CALC = 'max-h-[calc(100vh-300px)]';
const MAX_HEIGHT_CALC_MOBILE = 'max-h-[calc(100vh-250px)]';
const COL_WIDTH = 'w-1/5';
const LIMIT = 7; // Items per page

const Organizer = ({ setEditUserInfo, editUserInfo }) => {
    /* ****************************************************************
    *                            STATES                             *
    ******************************************************************/
    const userInfo = useSelector((state) => state.userInfo);
    const navigate = useNavigate();

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [organizers, setOrganizers] = useState([]);
    const [clickedOrganizer, setClickedOrganizer] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOrganizers, setFilteredOrganizers] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    /* ****************************************************************
    *                            SERVICES                             *
    ******************************************************************/
    /**
  * Fetches a list of organizers with pagination, and optionally appends the new organizers to the existing list.
  * Updates the state with the fetched organizer data and handles pagination.
  * 
  * @param {number} pageNum - The page number to fetch organizers from (defaults to 1).
  * @param {boolean} append - If true, appends the new organizers to the existing list (defaults to false).
  * @returns {void}
  */
    const getAllOrganizers = async (pageNum = 1, append = false) => {


        setLoading(true);
        console.log('Fetching page:', pageNum, 'Append:', append);

        try {
            const response = await apiConnecter(
                "GET",
                `${API_ENDPOINTS.REACT_APP_GET_ORGANIZER_LIST_END_POINT}?page=${pageNum}&limit=${LIMIT}`,
                null,
                { authorization: `Bearer ${userInfo.token}` }
            );

            const newOrganizers = response.data.result.organizers || [];
            console.log('Received organizers:', newOrganizers);

            if (append) {
                setOrganizers(prev => [...prev, ...newOrganizers]);
            } else {
                setOrganizers(newOrganizers);
            }

            // Check if there are more items to load
            setHasMore(newOrganizers.length === LIMIT);

        } catch (err) {
            console.error('Error fetching organizers:', err);
            setError({
                message: err?.response?.data?.message ?? err.message,
                title: "Error"
            });
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };
    /**
 * Deletes an organizer by their ID and updates the organizer list.
 * Displays a loading modal while deleting and shows a success/error message afterward.
 * 
 * @param {string} id - The unique ID of the organizer to delete.
 * @returns {void}
 */
    const deleteOrganizer = async (id) => {

        const toastId = Modal({
            title: "Deleting Organizer...",
            loading: true,
            loadingText: "Please wait while the organizer is being deleted...",
        });

        try {
            await apiConnecter(
                "DELETE",
                `${API_ENDPOINTS.REACT_APP_DELETE_ORGANIZER_END_POINT}/${id}`,
                null,
                { authorization: `Bearer ${userInfo.token}` }
            );

            setOrganizers(prevOrganizers =>
                prevOrganizers.filter(organizer => organizer._id !== id)
            );

            toast.dismiss(toastId);
            Modal({
                "message": "Organizer removed successfully", title: "Success"
            })
            setShowModal(false);

        } catch (err) {
            toast.dismiss(toastId);
            // toast.error(err?.response?.data?.message ?? "Failed to delete organizer");
            Modal({ "message": err?.response?.data?.message ?? err.message, title: "Error" });
            console.error('Error deleting organizer:', err);
        }
    };
    /**
     * Loads the next page of organizers for infinite scroll, checking if more organizers are available to load.
     * This function prevents loading if there are no more organizers to fetch or if a search term is active.
     * 
     * @returns {void}
     */
    // Load more organizers for infinite scroll
    const loadMoreOrganizers = () => {

        if (loading || !hasMore || searchTerm !== '') return;

        const nextPage = page + 1;
        console.log('Loading next page:', nextPage);
        setPage(nextPage);
        getAllOrganizers(nextPage, true);
    };

    /* ****************************************************************
    *                            HELPER FUNCTION                      *
    ******************************************************************/


    /**
     * Handles the edit button click by setting the organizer's information to be edited.
     * Sets the selected organizer and opens the form for editing.
     * 
     * @param {Object} organizer - The organizer object to be edited.
     * @returns {void}
     */
    const handleEditClick = (organizer) => {
        setEditUserInfo(organizer);
        setClickedOrganizer(organizer);
        setShowForm(true);
    };
    /**
     * Handles the delete button click by showing a confirmation modal and calling the delete organizer function.
     * 
     * @param {Object} organizer - The organizer object to be deleted.
     * @returns {void}
     */
    const handleDeleteClick = (organizer) => {
        setClickedOrganizer(organizer);
        Modal({
            title: "Do You Want To Delete?",
            message: `Are you sure you want to delete ${organizer.userName}?`,
            buttontxt: { accept: "Yes, Delete", decline: "No, Cancel" },
            onAccept: () => deleteOrganizer(organizer._id),
            onDecline: () => setClickedOrganizer(null),
            showAcceptDecline: true
        });
    };
    /**
 * Renders the events assigned to an organizer in a list of tags.
 * If no events are assigned, it shows a default message.
 * 
 * @param {Array} assignedEvents - An array of events assigned to the organizer.
 * @returns {JSX.Element} - A JSX element displaying the list of assigned events.
 */
    const renderEvents = (assignedEvents) => {
        return (
            <div className="flex flex-wrap gap-1 items-center justify-start">
                {assignedEvents?.length > 0 ? (
                    assignedEvents.map((event, eventIndex) => (
                        <span
                            key={eventIndex}
                            className="text-[var(--primary-text-color)] px-2 py-1 rounded-2xl text-xs border border-[var(--border-color)]"
                        >
                            {event?.eventName}
                        </span>
                    ))
                ) : (
                    <span className="text-[var(--primary-text-color)] text-sm">
                        No events assigned
                    </span>
                )}
            </div>
        );
    };


    /* ****************************************************************
      *                             EFFECT HOOKS                        *
      ******************************************************************/

    // Initial load - called once on mount
    useEffect(() => {
        getAllOrganizers(1, false);
    }, []); // Empty dependency array - runs once on mount

    // Client-side search filtering
    useEffect(() => {
        if (searchTerm === '') {
            setFilteredOrganizers(organizers);
        } else {
            const filtered = organizers.filter((organizer) => {
                const nameMatch = organizer.userName?.toLowerCase().includes(searchTerm.toLowerCase());
                const emailMatch = organizer.email?.toLowerCase().includes(searchTerm.toLowerCase());
                const eventMatch = organizer.assignedEvents?.some(event =>
                    event.eventName?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                return nameMatch || emailMatch || eventMatch;
            });
            setFilteredOrganizers(filtered);
        }
    }, [searchTerm, organizers]);

    // Show error/success modals
    useEffect(() => {
        if (error) {
            Modal({
                title: error.title || "Error",
                message: error.message,
            });
            const timer = setTimeout(() => setError(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        if (success) {
            Modal({
                title: success.title || "Success",
                message: success.message,
            });
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    return (
        <div className='flex flex-col w-full m-5 s'>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-semibold text-[var(--primary-text-color)]">Organizers</h2>
                    <p className="text-[var(--color-grey)] text-sm mt-1">
                        Manage event organizers and their permissions
                    </p>
                </div>
                <IconButton
                    icon={PlusIcon}
                    label="Add Organizer"
                    bgColor='bg-[var(--analytics-card-color)]'
                    onClick={() => {
                        setEditUserInfo(null);
                        setClickedOrganizer(null);
                        setShowForm(true);
                    }}
                    hoverColor="hover:bg-orange-600"
                />
            </div>



            <div className="bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] overflow-hidden">

                {/* DESKTOP/TABLET VIEW */}
                <div className="hidden md:block">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[var(--border-color)] sticky top-0 bg-[var(--color-surface-background)] z-10">
                                    <th className={`text-left py-4 px-6 text-[var(--primary-text-color)] text-sm font-medium ${COL_WIDTH}`}>
                                        Organizer
                                    </th>
                                    <th className={`text-left py-4 px-6 text-[var(--primary-text-color)] text-sm font-medium ${COL_WIDTH}`}>
                                        Email
                                    </th>
                                    <th className={`text-left py-4 px-6 text-[var(--primary-text-color)] text-sm font-medium ${COL_WIDTH}`}>
                                        Assigned Events
                                    </th>
                                    <th className={`text-left py-4 px-6 text-[var(--primary-text-color)]text-sm font-medium ${COL_WIDTH}`}>
                                        Status
                                    </th>
                                    <th className={`text-left py-4 px-6 text-[var(--primary-text-color)] text-sm font-medium ${COL_WIDTH} `}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                        </table>
                    </div>

                    <div
                        id="scrollableDivDesktop"
                        className={`overflow-x-auto overflow-y-auto ${MAX_HEIGHT_CALC}  custom-scrollbar`}
                    >
                        {loading && filteredOrganizers.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="loader"></div>
                            </div>
                        ) : (
                            <InfiniteScroll
                                dataLength={filteredOrganizers.length}
                                next={loadMoreOrganizers}
                                hasMore={searchTerm === '' && hasMore}
                                loader={
                                    <div className="text-center py-4">
                                        <div className="loader"></div>
                                    </div>
                                }

                                scrollableTarget="scrollableDivDesktop"
                            >
                                <table className="w-full">
                                    <tbody>
                                        {filteredOrganizers.map((organizer, index) => (
                                            <tr
                                                key={organizer._id || index}
                                                className="border-b border-[var(--border-color)] hover:bg-gray-750 transition-colors"
                                            >
                                                <td className={`py-4 px-6 ${COL_WIDTH}`}>
                                                    <div className="text-[var(--primary-text-color)] font-medium">
                                                        {organizer.userName}
                                                    </div>
                                                </td>
                                                <td className={`py-4 px-6 text-[var(--primary-text-color)] ${COL_WIDTH}`}>
                                                    {organizer.email}
                                                </td>
                                                <td className={`py-4 px-6 ${COL_WIDTH}`}>
                                                    {renderEvents(organizer.assignedEvents)}
                                                </td>
                                                <td className={`py-4 px-6 ${COL_WIDTH}`}>
                                                    <Badge
                                                        label={organizer?.status || 'N/A'}
                                                        bgColor={
                                                            (organizer?.status?.toLowerCase() === 'active')
                                                                ? 'bg-[#fafafa]'
                                                                : (organizer?.status?.toLowerCase() === 'completed')
                                                                    ? 'bg-[#262626]'
                                                                    : 'bg-transparent'
                                                        }
                                                        textColor={(organizer?.status?.toLowerCase() === 'active') ? 'text-black' : 'text-white'}
                                                        borderColor={(organizer?.status?.toLowerCase() === 'inactive') ? 'border-gray-700' : ''}
                                                    />
                                                </td>
                                                <td className={`py-4 px-6 ${COL_WIDTH}`}>
                                                    <div className="flex space-x-2">
                                                        <ActionButton
                                                            label="Edit"
                                                            onClick={() => handleEditClick(organizer)}
                                                            type="edit"
                                                        />
                                                        <ActionButton
                                                            label="Remove"
                                                            onClick={() => handleDeleteClick(organizer)}
                                                            type="remove"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </InfiniteScroll>
                        )}

                        {!loading && filteredOrganizers.length === 0 && (
                            <p className="text-center text-[var(--primary-text-color)] py-8">
                                {searchTerm ? 'No organizers match your search' : 'No organizers found'}
                            </p>
                        )}
                    </div>
                </div>

                {/* MOBILE VIEW */}
                <div
                    id="scrollableDivMobile"
                    className={`md:hidden p-4 gap-2  space-y-4 overflow-y-auto ${MAX_HEIGHT_CALC_MOBILE} custom-scrollbar`}
                >
                    {loading && filteredOrganizers.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="loader"></div>
                        </div>
                    ) : (
                        <InfiniteScroll
                            dataLength={filteredOrganizers.length}
                            next={loadMoreOrganizers}
                            hasMore={searchTerm === '' && hasMore}
                            loader={
                                <div className="text-center py-4">
                                    <div className="loader"></div>
                                </div>
                            }

                            scrollableTarget="scrollableDivMobile"
                        >
                            {filteredOrganizers.map((organizer, index) => (
                                <div
                                    key={organizer._id || index}
                                    className="bg-[var(--color-surface-background)] p-4 mb-2  rounded-lg border border-[var(--border-color)]"
                                >
                                    <div className="text-[var(--primary-text-color)] font-bold text-lg mb-1">
                                        {organizer.userName}
                                    </div>
                                    <div className="text-[var(--primary-text-color)] text-sm mb-3">
                                        {organizer.email}
                                    </div>

                                    <div className="mb-3">
                                        <span className="text-[var(--primary-text-color)] text-xs uppercase font-bold block mb-1">
                                            Assigned Events:
                                        </span>
                                        {renderEvents(organizer.assignedEvents)}
                                    </div>
                                    <div className='mb-2'>
                                        <Badge
                                            label={organizer?.status || 'N/A'}
                                            bgColor={
                                                (organizer?.status?.toLowerCase() === 'active')
                                                    ? 'bg-[#fafafa]'
                                                    : (organizer?.status?.toLowerCase() === 'completed')
                                                        ? 'bg-[#262626]'
                                                        : 'bg-transparent'
                                            }
                                            textColor={(organizer?.status?.toLowerCase() === 'active') ? 'text-black' : 'text-[var(--primary-text-color)]'}
                                            borderColor={(organizer?.status?.toLowerCase() === 'upcoming') ? 'border-gray-700' : ''}
                                        />

                                    </div>
                                    <div className="flex justify-end space-x-2 pt-2 border-t border-gray-800">

                                        <ActionButton
                                            label="Edit"
                                            onClick={() => handleEditClick(organizer)}
                                            type="edit"
                                        />
                                        <ActionButton
                                            label="Remove"
                                            onClick={() => handleDeleteClick(organizer)}
                                            type="remove"
                                        />
                                    </div>
                                </div>
                            ))}


                        </InfiniteScroll>
                    )}

                    {!loading && filteredOrganizers.length === 0 && (
                        <p className="text-center text-gray-400 py-8">
                            {searchTerm ? 'No organizers match your search' : 'No organizers found'}
                        </p>
                    )}
                </div>
            </div>
            {/* CreateOrganizer Form */}
            {showForm && (
                <CreateOrganizerForm
                    organizerToUpdate={clickedOrganizer}
                    onCancel={() => setShowForm(false)}
                    setSuccess={setSuccess}
                    setError={setError}
                    getAllOrganizers={getAllOrganizers}
                />
            )}
        </div>
    );
};

export default Organizer;